import { Component, EventEmitter, Output, effect, inject, signal } from '@angular/core';
import {
  ComparativeTableQuotationItem,
  QuotationColumn,
  TableQuotationsComparativeService,
} from '../../../services/table-quotations-comparative.service';
import { EnumService } from '../../../services/enum.service';
import { EnumValues } from '@card-software/library';
import { ConfirmationService } from 'primeng/api';
import { QuotationProxyService } from '../../../proxies/order/quotation.proxy.service';
import { LocaleHelperService } from '../../../services/locale-helper.service';
import { ExternalRequestEntry } from '../../../models/requisitions/interfaces/external-request-entry';
import { PurchaseOrder, PurchaseOrderSearchCommand } from '../../../models/purchase-order/purchase-order';
import { PurchaseOrderProxyService } from '../../../proxies/order/purchase-order.proxy.service';
import { GlobalEntityDefinition } from '@card-software/nexus-utilities';

@Component({
  selector: 'app-requisition-items-table',
  templateUrl: './requisition-items-table.component.html',
  styleUrl: './requisition-items-table.component.scss',
})
export class RequisitionItemsTableComponent {
  @Output() editQuotation = new EventEmitter<string>();
  uomValues: EnumValues = {};
  currencyValues: EnumValues = {};

  purchaseOrdersRelatedQuotation = signal<Map<string, PurchaseOrder[]>>(new Map());

  public tableService = inject(TableQuotationsComparativeService);
  private enumService = inject(EnumService);
  private confirmationService = inject(ConfirmationService);
  private quotationProxyService = inject(QuotationProxyService);
  public localeHelperService = inject(LocaleHelperService);
  private purchaseOrderProxyService = inject(PurchaseOrderProxyService);

  constructor() {
    this.uomValues = this.enumService.getEnum('UnitOfMeasure', 'itemEnums')?.values ?? {};
    this.currencyValues = this.enumService.getEnum('Currency', 'financeEnums')?.values ?? {};
    effect(() => {
      const columns = this.tableService.quotationsColumns();
      if (columns.length > 0) {
        this.searchPurchaseOrder();
      }
    });
  }

  getUomLabel(uom: number): string {
    return this.uomValues[uom] || '';
  }

  getStrictEntry(item: unknown) {
    return item as ExternalRequestEntry;
  }

  get quotationsColumns() {
    return this.tableService.quotationsColumns();
  }

  openImageInNewTab(presignedUrl: string): void {
    window.open(presignedUrl, '_blank');
  }

  public columnItemStrict(item: unknown) {
    return item as ComparativeTableQuotationItem;
  }

  deleteQuotation(quotationId: string): void {
    this.confirmationService.confirm({
      message: 'Are you sure you want to delete this quotation?',
      header: 'Confirm',
      icon: 'pi pi-exclamation-triangle',
      key: 'main',
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-secondary',
      accept: () => {
        this.quotationProxyService.deleteQuotation(quotationId).subscribe({
          next: (response) => {
            if (response.success) {
              this.tableService.removeQuotation(quotationId);

              this.purchaseOrdersRelatedQuotation.update((map) => {
                const newMap = new Map(map);
                newMap.delete(quotationId);
                return newMap;
              });
            }
          },
          error: (error) => {
            console.error(error);
          },
        });
      },
      reject: () => {
        return;
      },
    });
  }

  searchPurchaseOrder() {
    const entities = this.tableService.quotationsColumns().map((q) => ({
      reference: q.id,
      entityDefinitionCode: GlobalEntityDefinition.Quotation,
    }));

    if (entities.length === 0) return;

    entities.forEach((entity) => {
      const command: PurchaseOrderSearchCommand = {
        relatedEntities: [entity],
      };
      if (command) {
        this.purchaseOrderProxyService.searchPurchaseOrder(command, 1, 9999).subscribe({
          next: (response) => {
            if (response.success) {
              const purchaseOrders = response.data?.items ?? [];
              this.purchaseOrdersRelatedQuotation.update((map) => new Map(map).set(entity.reference, purchaseOrders));
            }
          },
        });
      }
    });
  }

  getPurchaseOrdersRelatedToQuotation(quotationId: string): number {
    const POs = this.purchaseOrdersRelatedQuotation().get(quotationId);

    if (!POs) return 0;

    return POs.length;
  }

  onEditQuotation(quotationId: string): void {
    this.editQuotation.emit(quotationId);
  }

  public redirectToPurchaseOrder(quote: QuotationColumn) {
    //route to purchase-order-details?id=new&quotationDetails={quote.id}&orderType=0 (Standard)
    const quotationDetails = quote.id;
    window.open(`/purchase-order-details?id=new&quotationDetails=${quotationDetails}&orderType=0`, '_blank');
  }

  /**
   * Calculate the grand total (with taxes) for a quotation column
   */
  public getGrandTotal(quote: QuotationColumn): number {
    return quote.items.reduce((sum, item) => sum + (item.totalPrice ?? 0), 0);
  }

  convertTotalPriceToDollars(totalPrice: number | null, exchangeRateToUSD: number): number {
    if (totalPrice === null) return 0;
    return totalPrice / exchangeRateToUSD;
  }
}
